import { useSyncedRef } from "@react-hookz/web";
import { useAtomValue, useSetAtom } from "jotai";
import first from "lodash/first";
import { startTransition, useCallback, useEffect, useMemo, useState } from "react";

import { useMixpanel } from "~/components/mixpanel";
import type { TrackPageViewFunction } from "~/components/tracking/atoms";
import { trackingAtom } from "~/components/tracking/atoms";
import * as gtm from "~/components/tracking/google-tag-manager/apis";
import { accountAtom } from "~/lib/account";
import { useCanonicalUrl, useDetectAdBlock, useMyNews } from "~/lib/hooks";
import { rosettaAtom } from "~/lib/rosetta";
import { isBotUserAgent } from "~/lib/utils";
import { useIsAutoRefreshed } from "~/pages/home/hooks";

type PendingPageView = {
  canonicalURL: string;
  data?: Record<string, unknown>;
};

export const useTrackingSetup = () => {
  const asyncAdBlockState = useDetectAdBlock();
  const { canonicalUrl: canonicalURL } = useCanonicalUrl();

  const { isLoggedIn, loginType } = useAtomValue(accountAtom);
  const isBot = isBotUserAgent();
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const asyncMixpanelState = useMixpanel();
  const { hasEnoughFollowedItems } = useMyNews();
  const { isAutoRefreshed, removeIsAutoRefreshed } = useIsAutoRefreshed();

  const variables = useMemo(() => {
    const userRole =
      asyncRosettaState?.result?.tracking?.userRole ??
      (isLoggedIn ? "authenticated user" : "anonymous user");
    const mixpanelId = asyncMixpanelState?.result?.get_distinct_id() as string | undefined;
    const adBlocker =
      asyncAdBlockState.result === undefined || asyncAdBlockState.result ? "yes" : "no";

    const product =
      (asyncRosettaState?.result?.user?.subscriptionProducts ?? []).length > 0
        ? first(asyncRosettaState?.result?.user?.subscriptionProducts)
        : null;

    return {
      adBlocker,
      autoRefresh: isAutoRefreshed ? "Yes" : "No",
      canonicalURL,
      isBot,
      loginType,
      myNewsSetup: hasEnoughFollowedItems ? "Yes" : "No",
      subscribe: {
        mixpanelId,
        product,
      },
      userRole,
    };
  }, [
    asyncAdBlockState.result,
    asyncMixpanelState?.result,
    asyncRosettaState?.result?.tracking?.userRole,
    asyncRosettaState?.result?.user?.subscriptionProducts,
    canonicalURL,
    hasEnoughFollowedItems,
    isAutoRefreshed,
    isBot,
    isLoggedIn,
    loginType,
  ]);
  const latestVariables = useSyncedRef(variables);

  // Keep pending page view information before all async states are fulfilled
  // Execute all pending page view when all async states are fulfilled and when the app is terminating
  // eslint-disable-next-line react/hook-use-state
  const [, setPendingPageViews] = useState<PendingPageView[]>([]);

  const isAllAsyncVariablesFulfilled = [
    asyncAdBlockState,
    asyncRosettaState,
    asyncMixpanelState,
  ].every(asyncState => asyncState?.error !== undefined || asyncState?.result !== undefined);
  const latestIsAllAsyncVariablesFulfilled = useSyncedRef(isAllAsyncVariablesFulfilled);

  const sendAllPendingPageView = useCallback(() => {
    setPendingPageViews(pendingPageViews => {
      pendingPageViews.forEach(pendingPageView => {
        const { canonicalURL, data } = pendingPageView;
        gtm.trackPageView({
          ...data,
          ...latestVariables.current,
          canonicalURL,
        });
      });
      return [];
    });
  }, [latestVariables]);

  // Execute all pending page view when all async states are fulfilled
  // Clear session storage value of `is_auto_refreshed`
  useEffect(() => {
    if (!isAllAsyncVariablesFulfilled) return;
    sendAllPendingPageView();
    removeIsAutoRefreshed();
  }, [isAllAsyncVariablesFulfilled, removeIsAutoRefreshed, sendAllPendingPageView]);

  // Execute all pending page view when the window is unloaded
  useEffect(() => {
    window.addEventListener("beforeunload", sendAllPendingPageView);
    return () => {
      window.removeEventListener("beforeunload", sendAllPendingPageView);
    };
  }, [latestVariables, sendAllPendingPageView]);

  const trackPageView: TrackPageViewFunction = useCallback(
    data => {
      if (latestIsAllAsyncVariablesFulfilled.current) {
        gtm.trackPageView({
          ...data,
          ...latestVariables.current,
        });
      } else {
        startTransition(() => {
          setPendingPageViews(pendingPageView => [
            ...pendingPageView,
            {
              canonicalURL: latestVariables.current.canonicalURL,
              data,
            },
          ]);
        });
      }
    },
    [latestIsAllAsyncVariablesFulfilled, latestVariables],
  );

  const setTracking = useSetAtom(trackingAtom);
  useEffect(() => {
    setTracking({ trackPageView });
  }, [setTracking, trackPageView]);
};
